import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap5';
import {
  Link, useLocation, useParams,
} from 'react-router-dom';

import routes from 'app/routes';
import BlockWithCheckbox from 'components/new_ui/Items/BlockWithCheckbox';
import formatCurrency from 'components/utils/formatCurrency';
import useCustomServices from 'components/Common/PlanCustomServices/Hooks/useCustomServices';
import LoadingPlaceholder from 'components/new_ui/WaitLayers/LoadingPlaceholder';
import usePlan from 'components/Common/PolicyPlans/Hooks/usePlan';
import planTotal from '../../utils/planTotal';
import ServicesHeader from './ServicesHeader';
import ShortSummary from '../Widgets/ShortSummary';

export default function Services({ enterpriseId }) {
  const { state } = useLocation();
  const { planId } = useParams();

  const originalPlan = state?.plan || usePlan(enterpriseId, planId).plan;
  const [plan, setPlan] = useState(originalPlan);

  const { mark, title: abbr } = plan ? plan.attributes.currency : {};

  if (plan === null) {
    return (
      <>
        <ServicesHeader />

        <section>
          <LoadingPlaceholder text="Loading plan..." />
        </section>
      </>
    );
  }
  const { services } = useCustomServices('q[required_for_manager_false]=1');

  const selectedTopics = plan.attributes.monthly_custom_records.map((item) => item.name);

  useEffect(() => {
    setPlan(originalPlan);
  }, [originalPlan]);

  const onServiceClicked = (serviceId) => {
    let selectedServices = plan.attributes.monthly_custom_records;
    const service = services.find((item) => item.id === serviceId);
    if (service) {
      if (selectedServices.find((item) => item.name === service.attributes.name)) {
        selectedServices = selectedServices.filter((item) => item.name !== service.attributes.name);
      } else {
        selectedServices = [...selectedServices, {
          id: service.id,
          name: service.attributes.name,
          price: service.attributes.price,
        }];
      }

      const newPlan = {
        ...plan,
        attributes: {
          ...plan.attributes,
          monthly_custom_records: selectedServices,
        },
      };

      setPlan({
        ...newPlan,
        attributes: {
          ...newPlan.attributes,
          monthly_total: planTotal({
            plan: newPlan,
            membersCovered: newPlan.attributes.employees_covered,
            billingType: newPlan.attributes.payment_mode,
          }),
        },
      });
    }
  };

  return (
    <>
      <ServicesHeader />

      <section>
        <Row>
          <Col md={7} style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <h3>Services</h3>

            <p>Select any of the services below to add your policy.</p>

            {services === null && <LoadingPlaceholder text="Loading services..." className="pt-5" /> }

            {services && services.map((service) => (
              <BlockWithCheckbox
                key={service.id}
                checkboxValue={service.id}
                title={service.attributes.name}
                description={(
                  <>
                    <b>
                      {mark}
                      {formatCurrency(service.attributes.currency_prices
                        .find((item) => item.currency === abbr)?.price, abbr)}
                    </b>
                    {' '}
                    per month
                  </>
                )}
                checked={selectedTopics.includes(service.attributes.name)}
                onChange={onServiceClicked}
              />
            ))}
          </Col>

          <Col md={5}>
            <h3>Your plan summary</h3>
            <p>Here&apos;s a summary of your plan so far.</p>

            <ShortSummary plan={plan} />
          </Col>
        </Row>
      </section>

      <footer className="d-md-flex flex-row-reverse">
        <div className="mb-3 mb-md-0 ms-md-3">
          <Link
            to={routes.managers.plans.edit.summary(planId)}
            className="btn btn-primary w-100"
            state={{ plan }}
          >
            Continue
          </Link>
        </div>

        <div className="flex-grow-1 d-flex justify-content-between">
          <Link
            to={routes.managers.plans.edit.plan_details(planId)}
            className="btn btn-primary btn-reverse"
            state={{ plan }}
          >
            Previous
          </Link>

          <Link
            to={routes.managers.policy.index}
            className="btn btn-outline-primary"
          >
            Cancel
          </Link>
        </div>
      </footer>
    </>
  );
}

Services.propTypes = {
  enterpriseId: PropTypes.number.isRequired,
};
